import Vue from 'vue';
import { DollarApollo } from 'vue-apollo/types/vue-apollo';
import { NORWEGIAN_POST_CITY } from '@/queries/PostalCode';

export interface IPostalAddress {
  zip?: string | null;
  city?: string | null;
  countryCode?: string | null;
}

// The city is filled only for a Norwegian address. An address without a
// country is not assumed to be Norwegian: a foreign zip is often typed before
// the country is picked. Every form applies its own default country before its
// zip field is shown, so an empty country is one the user has not chosen.
const isNorwegianZip = ({ zip, countryCode }: IPostalAddress): boolean =>
  countryCode === 'NO' && /^\d{4}$/.test(zip || '');

const cityToSet = (
  city: string | null,
  address: IPostalAddress,
  lookedUpZip: IPostalAddress['zip'],
): string | null => {
  // The user may have changed the zip or picked another country meanwhile.
  if(!city || address.zip !== lookedUpZip || !isNorwegianZip(address)) return null;

  return city.toLowerCase() === (address.city || '').toLowerCase() ? null : city;
};

// A failed lookup is not worth telling anyone about, the user types the city
// instead: no rejection (so no Bugsnag report for every zip typed), and no
// error message from the error link (`quietErrors`).
export async function postCityForZip(
  apollo: DollarApollo<Vue>,
  address: IPostalAddress,
): Promise<string | null> {
  if(!isNorwegianZip(address)) return null;

  const zip = address.zip;

  try {
    const { data } = await apollo.query<{ norwegianPostCity: string | null }>({
      query: NORWEGIAN_POST_CITY,
      variables: { zip },
      context: { quietErrors: true },
    });

    return cityToSet(data?.norwegianPostCity ?? null, address, zip);
  } catch {
    return null;
  }
}

export async function fetchPostCityForZip(
  serverHost: string | undefined,
  address: IPostalAddress,
): Promise<string | null> {
  if(!isNorwegianZip(address)) return null;

  const zip = address.zip;

  try {
    const response = await fetch(`${serverHost}/postal_codes/${zip}`);
    if(!response.ok) return null;

    const { city }: { city: string | null } = await response.json();

    return cityToSet(city, address, zip);
  } catch {
    return null;
  }
}
